import { BaseResponse } from '@/types/common';
import axiosInstance from './axiosInstance';

const baseUrl = `${SUPP_ADMIN_BASE_URL}/supp-management`;

/**
 * 素材类型枚举
 */
export enum MaterialType {
  /** 单图 */
  Single = 0,
  /** 双图 */
  Double = 1,
}

/**
 * 素材状态枚举
 */
export enum MaterialStatus {
  /** 上线 */
  Online = 0,
  /** 下线 */
  Offline = 1,
}

/**
 * 资源位素材详情接口
 */
export interface ResourceMaterial {
  /** 素材id */
  id: number;
  /** 资源位id */
  code: string;
  /** 资源位位置描述 */
  codeName: string;
  /** 描述 */
  materialDesc: string;
  /** 素材类型 0.单图 1.双图 */
  materialType: MaterialType;
  /** 排序 */
  sort: number;
  /** 状态 0.上线 1.下线 */
  status: MaterialStatus;
  /** 开始时间 */
  startTime: string;
  /** 结束时间 */
  endTime: string;
  /** 创建人 */
  createUserName: string;
  /** 素材图片 */
  positionMaterialImageResourceDTOList: MaterialImageResourceDTO[];
  /** 创建时间 */
  createTime?: string;
  /** 更新人 */
  updateUserName?: string;
  /** 更新人Id */
  updateUserId?: string;
  /** 更新时间 */
  updateTime?: string;
}

/**
 * 分页查询参数接口
 */
export interface ResourceMaterialPageParams {
  /** 当前页码 */
  currentPage: number;
  /** 每页大小 */
  pageSize: number;
  /** 状态筛选 */
  status?: MaterialStatus;
}

/**
 * 分页查询响应接口
 */
export interface ResourceMaterialPageResponse {
  /** 总记录数 */
  total: number;
  /** 当前页 */
  currentPage: number;
  /** 每页大小 */
  pageSize: number;
  /** 记录列表 */
  records: ResourceMaterial[];
}

/**
 * 素材图片资源DTO接口
 */
export interface MaterialImageResourceDTO {
  /** 素材id */
  materialId?: number;
  /** 素材url */
  imageUrl?: string;
  /** 跳转类型: 0.页面链接 1.话题详情页 2.作品详情页 */
  openType?: number;
  /** 跳转地址 */
  openUrl?: string;
}

/**
 * 创建/编辑资源位素材请求参数接口
 */
export interface ResourceMaterialRequest {
  /** 素材id (仅编辑时需要) */
  id?: number;
  /** 资源位id */
  code?: string;
  /** 描述 */
  materialDesc?: string;
  /** 素材类型 0.单图 1.双图 */
  materialType?: MaterialType;
  /** 预览图列表 */
  positionMaterialImageResourceDTOList?: MaterialImageResourceDTO[];
  /** 排序 */
  sort?: number;
  /** 状态 0.上线 1.下线 */
  status?: MaterialStatus;
  /** 开始时间 */
  startTime?: string;
  /** 结束时间 */
  endTime?: string;
  /** 创建人 */
  createUserName?: string;
  /** 创建人Id */
  createUserId?: string;
}

/**
 * 获取资源位素材详情
 * @param code 资源位编码
 * @param id 素材ID
 */
export const getResourceMaterialDetail = async (code: string, id: number) => {
  const resp = await axiosInstance.get<BaseResponse<ResourceMaterial>>(
    `${baseUrl}/resource/material/${code}/detail/${id}`
  );
  return resp.data;
};

/**
 * 资源位素材上架
 * @param code 资源位编码
 * @param id 素材ID
 */
export const upResourceMaterial = async (code: string, id: number) => {
  const resp = await axiosInstance.put<BaseResponse<void>>(
    `${baseUrl}/resource/material/${code}/up/${id}`
  );
  return resp.data;
};

/**
 * 资源位素材下架
 * @param code 资源位编码
 * @param id 素材ID
 */
export const downResourceMaterial = async (code: string, id: number) => {
  const resp = await axiosInstance.put<BaseResponse<void>>(
    `${baseUrl}/resource/material/${code}/down/${id}`
  );
  return resp.data;
};

/**
 * 获取资源位素材分页列表
 * @param code 资源位编码
 * @param params 查询参数
 */
export const getResourceMaterialPageList = async (
  code: string,
  params: ResourceMaterialPageParams
) => {
  const resp = await axiosInstance.post<
    BaseResponse<ResourceMaterialPageResponse>
  >(`${baseUrl}/resource/material/${code}/queryPageList`, params);
  return resp.data;
};

/**
 * 创建资源位素材
 * @param code 资源位编码
 * @param params 创建参数
 */
export const createResourceMaterial = async (
  code: string,
  params: ResourceMaterialRequest
) => {
  const resp = await axiosInstance.post<BaseResponse<void>>(
    `${baseUrl}/resource/material/${code}/add`,
    params
  );
  return resp.data;
};

/**
 * 编辑资源位素材
 * @param code 资源位编码
 * @param params 编辑参数
 */
export const updateResourceMaterial = async (
  code: string,
  params: ResourceMaterialRequest
) => {
  const resp = await axiosInstance.put<BaseResponse<void>>(
    `${baseUrl}/resource/material/${code}/edit`,
    params
  );
  return resp.data;
};
